import type { TFunctionType } from "@/core/hooks/use-typed-translation";
import { CheckCircle, XCircle } from "lucide-react";
import type { ShareablePreviewAction } from "./shareable-preview";

/** Options pour créer des boutons type approbation/confirmation */
export interface CreatePreviewActionsOptions {
  onConfirm: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  labels?: {
    confirm?: string;
    reject?: string;
    cancel?: string;
  };
  /**
   * Fonction de traduction (issue de useTypedTranslation) utilisée pour les
   * labels par défaut : common.confirm / common.reject / common.cancel.
   * Les `labels` explicites ont toujours la priorité.
   */
  t?: (key: TFunctionType) => string;
  /** Passe le bouton de confirmation en état de chargement (spinner + désactivé). */
  confirmLoading?: boolean;
  withIndicatorOnConfirm?: boolean;
  withPulseOnConfirm?: boolean;
  order?: "confirm-first" | "cancel-first";
}

/** Helper pour créer les boutons super (ValidationButton) type transaction/approbation */
export function createPreviewActions(
  options: CreatePreviewActionsOptions
): ShareablePreviewAction[] {
  const {
    onConfirm,
    onReject,
    onCancel,
    labels = {},
    t,
    confirmLoading = false,
    withIndicatorOnConfirm = true,
    withPulseOnConfirm = false,
    order = "confirm-first",
  } = options;

  const confirmLabel = labels.confirm ?? t?.("common.confirm") ?? "Confirmer";
  const rejectLabel = labels.reject ?? t?.("common.reject") ?? "Rejeter";
  const cancelLabel = labels.cancel ?? t?.("common.cancel") ?? "Annuler";

  const base =
    order === "cancel-first"
      ? { cancel: 0, confirm: 1, reject: 2 }
      : { confirm: 1, reject: 2, cancel: 3 };

  const confirmAction: ShareablePreviewAction = {
    label: confirmLabel,
    icon: CheckCircle,
    variant: "green",
    onClick: onConfirm,
    loading: confirmLoading,
    withIndicator: withIndicatorOnConfirm,
    withPulse: withPulseOnConfirm,
    order: base.confirm,
  };

  const rejectAction: ShareablePreviewAction | null = onReject
    ? {
        label: rejectLabel,
        icon: XCircle,
        variant: "red",
        onClick: onReject,
        order: base.reject,
      }
    : null;

  const cancelAction: ShareablePreviewAction | null = onCancel
    ? {
        label: cancelLabel,
        icon: XCircle,
        variant: "blue",
        onClick: onCancel,
        order: base.cancel,
      }
    : null;

  return [confirmAction, rejectAction, cancelAction].filter(
    (a): a is ShareablePreviewAction => a !== null
  );
}
